import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import Reveal from "@/components/Reveal";
import { STORE } from "@/data/store";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QUERY_TYPES = ["Product Enquiry", "Bulk / Wholesale Order", "Home Delivery", "Feedback", "Other"];

const initialForm = { name: "", phone: "", email: "", queryType: QUERY_TYPES[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim() || (!form.phone.trim() && !form.email.trim())) {
      toast.error("Please add your name, a message, and at least a phone or email.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received! We'll reach out to you shortly.");
      setForm(initialForm);
    } catch {
      toast.error("Couldn't send right now — please call or WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-border bg-card px-5 py-3.5 text-sm outline-none focus:border-terra transition-colors duration-300 placeholder:text-muted-foreground";

  return (
    <div data-testid="contact-page" className="pt-32 lg:pt-40 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-code text-xs uppercase tracking-[0.25em] text-terra mb-4">Contact Us</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.1] max-w-3xl">
            Talk directly to the family behind the counter.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mt-6">
            No call centres, no bots. Your message goes straight to the shop — and a real
            person answers.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-14">
          {/* Form */}
          <Reveal className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="rounded-[2rem] border border-border bg-card p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="contact-name" className="font-code text-[11px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Full Name *</label>
                  <input id="contact-name" data-testid="contact-name-input" className={inputCls} placeholder="Your name" value={form.name} onChange={update("name")} />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="font-code text-[11px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Phone</label>
                  <input id="contact-phone" data-testid="contact-phone-input" className={inputCls} placeholder="98765 43210" value={form.phone} onChange={update("phone")} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-code text-[11px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Email</label>
                  <input id="contact-email" type="email" data-testid="contact-email-input" className={inputCls} placeholder="you@example.com" value={form.email} onChange={update("email")} />
                </div>
                <div>
                  <label htmlFor="contact-query-type" className="font-code text-[11px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Query Type</label>
                  <select id="contact-query-type" data-testid="contact-query-type-select" className={inputCls} value={form.queryType} onChange={update("queryType")}>
                    {QUERY_TYPES.map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="font-code text-[11px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">Message *</label>
                  <textarea id="contact-message" rows={5} data-testid="contact-message-input" className={`${inputCls} resize-none`} placeholder="What can we keep aside for you?" value={form.message} onChange={update("message")} />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-form-submit-button"
                className="group mt-7 inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-4 font-sub text-sm font-semibold hover:bg-terra transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {sending ? "Sending..." : "Send Message"}
                <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <p className="font-code text-[11px] tracking-wide text-muted-foreground mt-4">
                We usually reply within a few hours during store timings.
              </p>
            </form>
          </Reveal>

          {/* Info cards */}
          <div className="lg:col-span-5 flex flex-col gap-5">
            <Reveal delay={0.08}>
              <a
                href={`mailto:${STORE.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-5 rounded-3xl border border-border bg-card p-6 hover:border-terra/60 transition-colors duration-300 group"
              >
                <span className="h-12 w-12 rounded-full bg-sage flex items-center justify-center shrink-0"><Mail size={19} className="text-terra" /></span>
                <span>
                  <span className="font-code text-[10px] uppercase tracking-[0.2em] text-muted-foreground block">Email Us</span>
                  <span className="font-sub font-semibold text-sm group-hover:text-terra transition-colors duration-300 break-all">{STORE.email}</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.14}>
              <a
                href={`tel:+${STORE.phoneRaw}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-5 rounded-3xl border border-border bg-card p-6 hover:border-terra/60 transition-colors duration-300 group"
              >
                <span className="h-12 w-12 rounded-full bg-sage flex items-center justify-center shrink-0"><Phone size={19} className="text-terra" /></span>
                <span>
                  <span className="font-code text-[10px] uppercase tracking-[0.2em] text-muted-foreground block">Call Us</span>
                  <span className="font-sub font-semibold text-sm group-hover:text-terra transition-colors duration-300">{STORE.phoneDisplay}</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <a
                href={`https://wa.me/${STORE.phoneRaw}?text=${encodeURIComponent("Namaste! I have a question about the store.")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="flex items-center gap-5 rounded-3xl border border-border bg-card p-6 hover:border-terra/60 transition-colors duration-300 group"
              >
                <span className="h-12 w-12 rounded-full bg-sage flex items-center justify-center shrink-0"><MessageCircle size={19} className="text-terra" /></span>
                <span>
                  <span className="font-code text-[10px] uppercase tracking-[0.2em] text-muted-foreground block">WhatsApp</span>
                  <span className="font-sub font-semibold text-sm group-hover:text-terra transition-colors duration-300">Chat with the store</span>
                </span>
              </a>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="rounded-3xl border border-border bg-card p-6" data-testid="contact-address-card">
                <div className="flex items-center gap-5">
                  <span className="h-12 w-12 rounded-full bg-sage flex items-center justify-center shrink-0"><MapPin size={19} className="text-terra" /></span>
                  <span>
                    <span className="font-code text-[10px] uppercase tracking-[0.2em] text-muted-foreground block">Visit the Store</span>
                    <span className="font-sub font-semibold text-sm leading-snug">{STORE.address}</span>
                  </span>
                </div>
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(STORE.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-directions-button"
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-foreground/25 px-5 py-2.5 font-sub text-xs font-semibold hover:border-terra hover:text-terra transition-colors duration-300"
                >
                  <MapPin size={13} /> Get Directions
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.32}>
              <div className="rounded-3xl border border-border bg-card p-6" data-testid="contact-hours-card">
                <div className="flex items-center gap-5">
                  <span className="h-12 w-12 rounded-full bg-sage flex items-center justify-center shrink-0"><Clock size={19} className="text-terra" /></span>
                  <span>
                    <span className="font-code text-[10px] uppercase tracking-[0.2em] text-muted-foreground block">Store Hours</span>
                    {STORE.hours.map((h) => (
                      <span key={h.days} className="font-sub font-semibold text-sm block">{h.days}: {h.time}</span>
                    ))}
                  </span>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  );
}
